import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import {
  Box, Paper, Typography, TextField, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Chip, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButton, ToggleButtonGroup, Stack
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export default function AppraisalsList() {
  const theme = useTheme();
  const role = localStorage.getItem('role') || 'employee';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [year, setYear] = useState('');
  const [status, setStatus] = useState('');
  const [scope, setScope] = useState(
    role === 'hr_admin' || role === 'ceo' ? 'all' : role === 'manager' ? 'team' : 'my'
  );

  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/appraisals', {
        params: { search, year, status, scope }
      });
      setRows(res.data.data || []);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    try {
      const res = await api.get(`/admin/appraisals/${id}`);
      setDetail(res.data);
      setOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary2?.main || '#2389ba'} 100%)`,
        color: '#fff', p: 3, borderRadius: 2, mb: 3
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Performance Appraisals</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mt: 2 }} alignItems="center">
          {/* Scope switcher (role-aware) */}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={scope}
            onChange={(_e, v) => v && setScope(v)}
            sx={{
              background: 'rgba(255,255,255,0.12)',
              '& .MuiToggleButton-root': { color: '#fff', borderColor: 'rgba(255,255,255,0.4)' },
              '& .Mui-selected': { background: 'rgba(0,0,0,0.18)' },
            }}
          >
            <ToggleButton value="my">My</ToggleButton>
            {role !== 'employee' && <ToggleButton value="team">Team</ToggleButton>}
            {(role === 'hr_admin' || role === 'ceo') && <ToggleButton value="all">All</ToggleButton>}
          </ToggleButtonGroup>

          <TextField size="small" label="Search name/code" value={search} onChange={(e)=>setSearch(e.target.value)}
            InputProps={{ style:{color:'#fff'} }} InputLabelProps={{ style:{color:'#fff'} }}
            sx={{ bgcolor:'rgba(255,255,255,0.12)', borderRadius:1, flex:1, minWidth:200 }} />
          <TextField size="small" label="Year" type="number" value={year} onChange={(e)=>setYear(e.target.value)}
            InputProps={{ style:{color:'#fff'} }} InputLabelProps={{ style:{color:'#fff'} }}
            sx={{ bgcolor:'rgba(255,255,255,0.12)', borderRadius:1, width:140 }} />
          <TextField size="small" label="HR Status" value={status} onChange={(e)=>setStatus(e.target.value)}
            InputProps={{ style:{color:'#fff'} }} InputLabelProps={{ style:{color:'#fff'} }}
            sx={{ bgcolor:'rgba(255,255,255,0.12)', borderRadius:1, width:180 }} />

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={load} sx={{ color:'#fff', borderColor:'rgba(255,255,255,0.6)' }}>Apply</Button>
            <Button variant="contained" onClick={()=>{ setSearch(''); setYear(''); setStatus(''); load(); }}>Reset</Button>
          </Stack>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ textAlign:'center', mt:4 }}>
          <CircularProgress/>
          <Typography sx={{ mt: 1 }}>Loading…</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                <TableCell>Employee</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Supervisor</TableCell>
                <TableCell>Overall</TableCell>
                <TableCell>HR Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center">No data</TableCell></TableRow>
              ) : rows.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.employee?.name || '—'}</TableCell>
                  <TableCell>{r.employee_code}</TableCell>
                  <TableCell>{r.supervisor?.name || '—'}</TableCell>
                  <TableCell>{r.overall_rating ?? '—'}</TableCell>
                  <TableCell><Chip size="small" label={r.hr_appraisal_status}/></TableCell>
                  <TableCell>{new Date(r.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" onClick={()=>openDetail(r.id)}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={()=>setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Appraisal Details</DialogTitle>
        {detail && (
          <DialogContent dividers>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {detail.appraisal.employee?.name} ({detail.appraisal.employee_code})
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Overall: <strong>{detail.appraisal.overall_rating ?? detail.avg_rating ?? '—'}</strong>
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Question</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Comments</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.appraisal.responses.map((x) => (
                  <TableRow key={x.id}>
                    <TableCell>{x.question?.title}</TableCell>
                    <TableCell>{x.applicability === 'YES' ? (x.rating ?? '—') : 'N/A' }</TableCell>
                    <TableCell>{x.comments || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
        )}
        <DialogActions><Button onClick={()=>setOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
